import React, { useEffect, useRef, useState } from 'react'
import styles from './RemainingSpinsBanner.module.css'
import {
    useFreeSpinsBonusesStore,
    useConfigurationStore,
    useLuckySpinsStore,
    useSpinButtonSectionStore,
    useCountdownFSBonusStore,
} from '@stores'

import infoLineIcon from '@assets/bonus-system/icon_InfoLine.png'
import infoLineIconCountdown from '@assets/bonus-system/icon_InfoLine_countdown.png'
import { formatMoney } from '@utils/money'
import {
    BaseGameType,
    BonusTypes,
    ChangeOpacityComponents,
    FreespinBonusCountdownEndedMessage,
    GameUIMessageType,
} from 'game-ui-types'
import { CloverAnimation, cn, useShrinkTextToFit } from '@amusnet/react-ui'
import useOpacityToggle from '@hooks/useOpacityToggle'
import { useChangeTabVisibility, useScaleToFit } from '@hooks'
import { getCountdownDisplayItems, getCountdownValuesFromBonus } from '@utils/bonus'
import { useTranslation } from '@amusnet/translations/react'
import { CountdownCounterDisplayItems, CountdownCounterValues } from '@components/CountdownFreeSpinsBonus/types'
import freeSpinsInfoLineBackgroundConfigE from '@assets/bonus-system/free-spin-mode-info-line-background-config-E.svg'
import luckySpinsInfoLineBackgroundConfigE from '@assets/bonus-system/lucky-spin-mode-info-line-background-config-E.png'
import betButtonConfigE from '@assets/bet-button-config-e.svg'

const RemainingSpinsBanner: React.FC = () => {
    const fsRemainingSpins = useFreeSpinsBonusesStore((state) => state.remainingSpins)
    const fsTotalWin = useFreeSpinsBonusesStore((state) => state.totalWin)
    const fsActiveBet = useFreeSpinsBonusesStore((state) => state.activeBet)

    const isLuckySpinsMode = useLuckySpinsStore((state) => state.isIntro)
    const lsRemainingSpins = useLuckySpinsStore((state) => state.remainingSpins)
    const lsTotalWin = useLuckySpinsStore((state) => state.totalWin)
    const lsActiveBet = useLuckySpinsStore((state) => state.activeBet)
    const cfsActiveBonus = useCountdownFSBonusStore((state) => state.activeBonus)

    const cfsTotalWin = useCountdownFSBonusStore((state) => state.totalWin)
    const cfsActiveBet = useCountdownFSBonusStore((state) => state.activeBet)

    const isAutoplaySectionOpened = useSpinButtonSectionStore((state) => state.isAutoplaySectionOpened)
    const isBetSectionOpened = useSpinButtonSectionStore((state) => state.isBetSectionOpened)

    const { t } = useTranslation()
    const gameType = useConfigurationStore((state) => state.gameType)
    const currency = useConfigurationStore((state) => state.currency)
    const currencyHideCents = useConfigurationStore((state) => state.currencyHideCents)
    const isSlotConfigE = gameType === BaseGameType.SLOT_CONFIG_E

    const betAmountRef = useRef<HTMLDivElement>(null)
    const remainingSpinsBannerRef = useRef<HTMLDivElement | null>(null)
    const freeSpinsInfoContainerRef = useRef<HTMLDivElement | null>(null)
    const freeSpinsLeftRef = useRef<HTMLDivElement | null>(null)
    const totalWinRef = useRef<HTMLDivElement | null>(null)
    const remainingSpinsBannerConfigPosition = useConfigurationStore(
        (state) => state.getComponentConfig('RemainingSpinsBanner').position
    )
    const isCFSMode = cfsActiveBonus?.type === BonusTypes.COUNTDOWN_FREE_SPIN
    const [countdown, setCountdown] = useState<CountdownCounterValues>(null)
    const countdownSounds = useRef<number[]>([])
    const hasCompleted = useRef(false)

    const bet = formatMoney(
        isCFSMode ? +cfsActiveBet : isLuckySpinsMode ? +lsActiveBet : +fsActiveBet,
        currencyHideCents
    )
    const formattedTotalWin = formatMoney(
        isCFSMode ? +cfsTotalWin : isLuckySpinsMode ? +lsTotalWin : +fsTotalWin,
        currencyHideCents
    )
    const remainingSpins = isLuckySpinsMode ? lsRemainingSpins : fsRemainingSpins

    useOpacityToggle(ChangeOpacityComponents.REMAINING_SPINS_BANNER, remainingSpinsBannerRef)

    const betAreaWidth = isSlotConfigE ? 156 : 136

    useShrinkTextToFit({
        refresh: [bet],
        designFontSize: 46,
        bounds: { width: betAreaWidth - 6 },
        textRef: betAmountRef,
    })

    const scaleFreeSpinsLeft = useScaleToFit(freeSpinsInfoContainerRef, freeSpinsLeftRef, [])
    const scaleTotalWin = useScaleToFit(freeSpinsInfoContainerRef, totalWinRef, [
        t('gameui.totalWin'),
        formattedTotalWin,
    ])

    const completeCountdownFS = () => {
        if (cfsActiveBonus && !hasCompleted.current) {
            const newCountdown = getCountdownValuesFromBonus(cfsActiveBonus)

            setCountdown(newCountdown)

            if (!newCountdown || (newCountdown.minutes === 0 && newCountdown.seconds === 0)) {
                hasCompleted.current = true

                const countdownEndedMessage: FreespinBonusCountdownEndedMessage = {
                    type: GameUIMessageType.FREESPIN_BONUS_COUNTDOWN_ENDED,
                    bonusId: cfsActiveBonus.id,
                }

                window.postMessage(countdownEndedMessage)
            }
        }
    }

    useChangeTabVisibility({
        onTabVisible: completeCountdownFS,
    })

    useEffect(() => {
        if (!cfsActiveBonus) {
            setCountdown(null)
            hasCompleted.current = false

            return
        }

        let lastProcessedSecond = -1

        countdownSounds.current = []
        hasCompleted.current = false

        const countdownTick = () => {
            const totalMs = +(cfsActiveBonus.expirationTime ?? 0) - Date.now()
            const currentTotalSeconds = Math.max(0, Math.floor(totalMs / 1000))

            if (currentTotalSeconds !== lastProcessedSecond) {
                lastProcessedSecond = currentTotalSeconds

                const newCountdown = getCountdownValuesFromBonus(cfsActiveBonus)

                setCountdown(newCountdown)

                if (!newCountdown) {
                    completeCountdownFS()

                    return
                }

                const { days, hours, minutes, seconds } = newCountdown
                const isLast5Seconds = days === 0 && hours === 0 && minutes === 0 && seconds <= 5 && seconds > 0

                if (isLast5Seconds && !countdownSounds.current.includes(seconds)) {
                    countdownSounds.current.push(seconds)
                    window.postMessage({ type: GameUIMessageType.COUNTDOWN_FS_BONUS_PLAY_SOUND })
                }
            }
        }
        const timerId = setInterval(countdownTick, 1000)

        countdownTick()

        return () => clearInterval(timerId)
    }, [isCFSMode])

    useEffect(() => {
        if (isLuckySpinsMode) {
            // send to game-platform for promo widget
            window.parent.postMessage({ event: 'luckySpinsStarted' }, '*')

            return () => window.parent.postMessage({ event: 'luckySpinsEnded' }, '*')
        }
    }, [isLuckySpinsMode])

    // TODO: remove once switching games during bonus spins is tested - VOLT-4375 + VOLT-4753
    useEffect(() => {
        window.parent.postMessage({ event: 'bonusSpinsStarted' }, '*')

        return () => window.parent.postMessage({ event: 'bonusSpinsEnded' }, '*')
    }, [])

    const renderCountdown = () => {
        const { days, hours, minutes, seconds } = countdown ?? { days: 0, hours: 0, minutes: 0, seconds: 0 }

        const displayItems: CountdownCounterDisplayItems = [
            { val: days, label: t('gameui.bonusSystem.timeLeftDays') },
            { val: hours, label: t('gameui.bonusSystem.timeLeftHours') },
            { val: minutes, label: t('gameui.bonusSystem.timeLeftMinutes') },
            { val: seconds, label: t('gameui.bonusSystem.timeLeftSeconds') },
        ]

        const { firstStringDigits, firstStringLabel, secondStringDigits, secondStringLabel } =
            getCountdownDisplayItems(displayItems)

        const isLast10Seconds = minutes === 0 && seconds <= 10 && hours === 0 && days === 0
        const isLast5Seconds =
            (minutes === 0 && seconds <= 5 && seconds >= 0 && hours === 0 && days === 0) || seconds < 0

        return (
            <span className={cn('pl-2', isLast5Seconds ? 'text-red-600 mb-[-4px]' : 'text-yellow-400 mb-[-3px]')}>
                <span className="text-[30px]">{firstStringDigits}</span>
                <span>{firstStringLabel}</span>
                <span
                    key={secondStringDigits}
                    className={cn(
                        isLast10Seconds && seconds > 0 ? styles.animateBlink : '',
                        isLast5Seconds ? 'text-[36px]' : 'text-[30px]'
                    )}
                >
                    {secondStringDigits}
                </span>
                <span>{secondStringLabel}</span>
            </span>
        )
    }

    const configEBackground = isLuckySpinsMode
        ? luckySpinsInfoLineBackgroundConfigE
        : freeSpinsInfoLineBackgroundConfigE

    return (
        <div
            style={{
                ...remainingSpinsBannerConfigPosition,
                zIndex: isAutoplaySectionOpened || isBetSectionOpened ? 10 : 11,
            }}
            className="absolute left-1/2 -translate-x-1/2 overflow-hidden"
            ref={remainingSpinsBannerRef}
        >
            <div
                className={cn(
                    'flex flex-row items-center justify-start h-[96px] text-white transform origin-bottom bg-no-repeat bg-center',
                    isSlotConfigE
                        ? 'w-[500px]'
                        : isLuckySpinsMode
                          ? "bg-[url('/src/assets/lucky-spins/lucky-spin-mode-info-line-background.png')] w-[728px]"
                          : "bg-[url('/src/assets/bonus-system/background-infoline.svg')] w-[738px]"
                )}
                style={isSlotConfigE ? { backgroundImage: `url("${configEBackground}")` } : undefined}
            >
                <div
                    className={cn('text-center', isSlotConfigE && 'p-[31px] bg-contain bg-center bg-no-repeat')}
                    style={{
                        width: `${betAreaWidth}px`,
                        ...(isSlotConfigE ? { backgroundImage: `url("${betButtonConfigE}")` } : {}),
                    }}
                >
                    <div
                        className="text-[24px] text-center font-bold text-yellow-400 leading-none"
                        style={isSlotConfigE ? { fontSize: '20px' } : undefined}
                    >
                        {t('gameui.bet')}
                    </div>
                    <div
                        className="text-[46px] text-center font-bold text-white leading-none inline-block"
                        ref={betAmountRef}
                        style={isSlotConfigE ? { lineHeight: '40px' } : undefined}
                    >
                        {bet}
                    </div>
                    <div className="text-[18px] text-center font-bold text-yellow-400 leading-[16px]">{currency}</div>
                </div>

                <div className={cn('w-auto', isSlotConfigE ? 'mx-0' : isLuckySpinsMode ? 'mx-[15px]' : 'mx-[35px]')}>
                    {isLuckySpinsMode ? (
                        <div
                            className={cn('flex justify-center align-center', isSlotConfigE ? 'w-[75px]' : 'w-[140px]')}
                        >
                            <div style={{ scale: isSlotConfigE ? '0.3' : '0.35' }}>
                                <CloverAnimation />
                            </div>
                        </div>
                    ) : (
                        <img src={isCFSMode ? infoLineIconCountdown : infoLineIcon} alt="Info line icon" />
                    )}
                </div>

                <div className="w-[420px]" ref={freeSpinsInfoContainerRef}>
                    <div
                        className="text-[24px] h-[50px] font-bold inline-block mt-[-5px] mb-[-5px]"
                        ref={freeSpinsLeftRef}
                        style={{ zoom: scaleFreeSpinsLeft }}
                    >
                        <span className="text-gray-300 whitespace-nowrap inline-block mt-[14px]">
                            {isCFSMode
                                ? t('gameui.bonusSystem.timeLeftLabel')
                                : isLuckySpinsMode
                                  ? t('gameui.bonusSystem.luckySpinsLeft')
                                  : t('gameui.bonusSystem.freeSpinsLeft')}
                            :
                        </span>
                        {isCFSMode ? (
                            renderCountdown()
                        ) : (
                            <span className="text-yellow-400 pl-2 text-[30px] mb-[-3px]">{remainingSpins}</span>
                        )}
                    </div>
                    <div
                        className="text-[24px] font-bold inline-block whitespace-nowrap"
                        ref={totalWinRef}
                        style={{ zoom: scaleTotalWin }}
                    >
                        <span className="text-gray-300">{t('gameui.totalWin')}:</span>
                        <span className="text-yellow-400 px-2 text-[30px]">{formattedTotalWin}</span>
                        <span className="text-gray-300"> {currency}</span>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default RemainingSpinsBanner
